/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Container, CircularProgress, Typography, Button, Box, Grid2 } from '@mui/material';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { withStyles } from '@mui/styles';
import { FormProvider, useFieldArray, useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import _ from 'lodash';
import FlagIcon from '@mui/icons-material/Flag';
import { Alert as MuiAlert } from '@mui/lab';
import { useHistory } from 'react-router-dom';
import ExpenseButtons from '../Components/Expense/ExpenseButtons';
import ExpenseReceiptUploadForm from '../Components/Expense/ExpenseReceiptUploadForm';
import ExpenseEntryHeader from '../Components/Expense/ExpenseEntryHeader';
import {
    getExpenseById,
    getExpenseCategories,
    getStatusesAllowed,
    updateExpense,
} from '../Services/ExpenseService';
import { getDivisions } from '../Services/Common';
import Alert from '../Components/Alert';
import ExpenseTabs from '../Components/Expense/Tabs/ExpenseTabs';
import ExpenseTab from '../Components/Expense/Tabs/ExpenseTab';
import ExpenseTabLabel from '../Components/Expense/Tabs/ExpenseTabLabel';
import ExpenseTabPanel from '../Components/Expense/Tabs/ExpenseTabPanel';
import BookingDetailsForm from '../Components/Expense/BookingDetailsForm';
import VaultDetailsForm from '../Components/Expense/VaultDetailsForm';
import ExpenseSummary from '../Components/Expense/ExpenseSummary';
import InternalNotesForm from '../Components/Expense/InternalNotesForm';
import {
    ExpenseStatusEnum,
    ExpenseStatusValuesEnum,
} from '../Components/Expense/ExpenseStatus.enum';
import ExpenseNeedsReviewAlert from '../Components/Expense/ExpenseNeedsReviewAlert';
import { COLORS } from '../../utils/Application_Constants';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import ChargeDetailsForm from '../Components/Expense/ChargeDetailsForm';
import { createExpenseFromExpenseEntries } from '../Utils/ExpenseUtil';
import { uploadImages } from '../Utils/UploadImages';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import { ValidationModeContext } from '../../contexts/VendorPaymentValidationModeContext';
import ValidationModeEnum from '../Components/Expense/ValidationMode.enum';
import { ExpenseModeEnum } from '../Components/Expense/ExpenseMode.enum';

const styles = (theme) => ({
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '1.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    formGridContainer: {
        gap: 32,
    },
    summary: {
        position: 'sticky',
        top: '5rem',
        paddingTop: '.5rem',
    },
    warningMessage: {
        color: COLORS.LT_SUNFLOWER,
    },
    iconContainer: {
        position: 'absolute',
        top: 8,
        right: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    flagIcon: {
        cursor: 'pointer',
    },
    viewBookingButton: {
        minWidth: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
    },
    tabPanel: {
        position: 'relative',
    },
    alertContainer: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(4),
        },
        paddingLeft: '.5rem',
    },
});

const defaultExpense = {
    bookingId: null,
    clinicianId: '',
    clinicianFirstName: '',
    clinicianLastName: '',
    clientId: '',
    division: '',
    state: '',
    billType: '',
    category: '',
    type: '',
    currency: 'USD',
    amount: '',
    reimbursedAmount: '',
    accountingProcessingNotes: '',
    label: 'Expense',
    mileage: '',
    status: 'New',
    salespersonName: '',
    recruiterName: '',
    payDate: null,
    expenseClass: 'Reimbursement',
    splitTransactionComment: '',
    source: 'LtAssociate',
    clinicianFacingComments: '',
    commentsFromClinician: '',
    merchantName: '',
    highPriority: false,
};

const VaultPayment = (props) => {
    const { classes } = props;
    const [isLoading, setIsLoading] = useState(false);
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser } = useContext(AdminAuthContext);
    const [expenseCategories, setExpenseCategories] = useState(null);
    const [divisions, setDivisions] = useState([]);
    const [statusesAllowed, setStatusesAllowed] = useState([]);
    const { id } = useParams();
    const history = useHistory();
    const [alert, setAlert] = useState();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [removeTabs, setRemoveTabs] = useState(false);
    const backLink = id ? 'All Expenses' : 'Expense Types';
    const title = id ? 'Review Vault Charge' : 'Vault Expense Entry';
    const expensesPath = id ? '/expenses/vault/review' : '/expenses';
    const [originalExpenseEntries, setOriginalExpenseEntries] = useState([]);
    const [originalPrimaryExpense, setOriginalPrimaryExpense] = useState();
    const isViewOnly = true;
    const isAnyExpensePosted = useMemo(() => {
        if (originalExpenseEntries?.length > 0) {
            return originalExpenseEntries.some((entry) => entry.status === 'Posted');
        }
        return false;
    }, [originalExpenseEntries]);
    const defaultValues = {
        fileUploads: [{ newFiles: [], existingFiles: [] }],
        expenseEntries: [defaultExpense],
    };
    const methods = useForm({ shouldUnregister: false, defaultValues });
    const {
        handleSubmit,
        reset,
        control,
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = methods;
    const {
        fields: expenseEntries,
        append,
        remove,
    } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormContext)
        name: 'expenseEntries', // unique name for your Field Array
        keyName: 'expenseEntryId',
    });
    const watchExpenseEntries = watch(`expenseEntries`);
    const isCurrentApproved =
        originalExpenseEntries[activeTabIndex]?.status === ExpenseStatusValuesEnum.Approved;
    const isCurrentPosted =
        originalExpenseEntries[activeTabIndex]?.status === ExpenseStatusValuesEnum.Posted;
    const [showBalanceError, setShowBalanceError] = useState(false);
    const [showBalanceWarning, setShowBalanceWarning] = useState(false);
    const hasErrors = !!Object.keys(methods.formState.errors).length;
    const [assignmentsByIndex, setAssignmentsByIndex] = useState({});
    const [validationMode, setValidationMode] = useState(ValidationModeEnum.Save);
    const [pendingSave, setPendingSave] = useState(false);
    const [pdfSuccessMessage, setPdfSuccessMessage] = useState(null);

    const handleAssignmentChange = (index, assignment) => {
        setAssignmentsByIndex((prev) => ({
            ...prev,
            [index]: assignment,
        }));
    };

    useEffect(() => {
        Promise.all([getDivisions(), getStatusesAllowed('Vault'), getExpenseCategories('Vault')])
            .then(async ([divs, statuses, cats]) => {
                setExpenseCategories(cats);
                setDivisions(divs);
                setStatusesAllowed(statuses);
            })
            .catch((error) => {
                console.error(error);
            });

        if (id) {
            fetchExpenseById();
        }
    }, []);

    useEffect(() => {
        if (watchExpenseEntries.length > 1) {
            const firstExpenseComment = watchExpenseEntries[0]?.commentsFromClinician || '';

            watchExpenseEntries.forEach((expense, idx) => {
                if (idx !== 0 && expense.commentsFromClinician !== firstExpenseComment) {
                    setValue(`expenseEntries[${idx}].commentsFromClinician`, firstExpenseComment);
                }
            });
        }
    }, [watchExpenseEntries, setValue]);

    // This was added to address a rendering issue
    // It turns off the TabPanel Display removes the item and then turns back on the display
    useEffect(() => {
        if (removeTabs) {
            resetImages(activeTabIndex);
            remove(activeTabIndex);
            setActiveTabIndex(activeTabIndex - 1);
            setRemoveTabs(false);
        }
    }, [removeTabs]);

    const fetchExpenseById = async () => {
        try {
            setIsLoading(true);
            const res = await getExpenseById(id);
            const listExpenses = [];
            const aOriginalExpenseEntries = [];
            const aFileUploads = [];
            listExpenses.push(res);

            for (let i = 0; i < listExpenses.length; i++) {
                const formattedValues = formatResponse(listExpenses[i]);
                // Reset back to Null as empty string on the as the default was causing issues
                formattedValues.payDate =
                    _.isString(formattedValues.payDate) && formattedValues.payDate.length === 0
                        ? null
                        : formattedValues.payDate.substring(0, 10);
                aOriginalExpenseEntries.push({
                    ...formattedValues,
                    label: listExpenses[i].label ? listExpenses[i].label : `Expense ${i}`,
                });
                if (
                    listExpenses[i] &&
                    listExpenses[i].expenseImages &&
                    listExpenses[i].expenseImages.length >= 0
                ) {
                    aFileUploads.push({
                        newFiles: [],
                        existingFiles: listExpenses[i].expenseImages,
                    });
                } else {
                    aFileUploads.push({ newFiles: [], existingFiles: [] });
                }
            }
            setOriginalExpenseEntries(aOriginalExpenseEntries);
            setOriginalPrimaryExpense(aOriginalExpenseEntries[0]);
            const defaultValues = {
                fileUploads: aFileUploads,
                expenseEntries: aOriginalExpenseEntries,
            };
            reset(defaultValues);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    const formatResponse = (res) =>
        Object.fromEntries(
            Object.entries(res).map(([key, value]) => [key, value === null ? '' : value])
        );

    const clearAllHiddenErrors = () => {
        const aExpenseEntries = getValues('expenseEntries');
        aExpenseEntries.map((element, index) => {
            clearExpenseHiddenErrors(element, index);
        });
    };

    const validateSubmit = async () => {
        //TO DO IN FUTURE STORY
    };

    const validateSave = async () => {
        const isApproved = watchExpenseEntries.some(
            (entry) => entry.status === ExpenseStatusValuesEnum.Approved
        );
        setValidationMode(isApproved ? ValidationModeEnum.Submit : ValidationModeEnum.Save);
        if (errors.balance) {
            setShowBalanceError(true);
        } else {
            clearErrors('balance');
            setShowBalanceError(false);
            setPendingSave(true);
        }
    };

    useEffect(() => {
        if (pendingSave) {
            handleSubmit(onSave)();
            setPendingSave(false);
        }
    }, [pendingSave]);

    const getStatusKey = (value) =>
        Object.keys(ExpenseStatusEnum).find((key) => ExpenseStatusEnum[key].value === value);

    const onSave = async (data, showSuccessPopup = true) => {
        try {
            setIsLoading(true);
            const newExpense = createExpenseFromExpenseEntries(data.expenseEntries, true);
            let expenseResponse;

            if (newExpense.id) {
                expenseResponse = await updateExpense(newExpense.id, newExpense);
            } else {
                throw new Error('Something went wrong. Please refresh the page and try again.');
            }

            const fileUploads = getValues(`fileUploads`);
            const hasAnyFiles =
                Array.isArray(fileUploads) &&
                fileUploads.some(
                    (upload) =>
                        (upload.newFiles && upload.newFiles.length > 0) ||
                        (upload.existingFiles && upload.existingFiles.length > 0)
                );
            if (hasAnyFiles) {
                await uploadImages(fileUploads, expenseResponse, ExpenseModeEnum.Payment);
            }
            if (showSuccessPopup) {
                history.push({
                    pathname: '/expenses/submit',
                    state: { prevPage: '/expenses/vault/review' },
                });
            }
            // Do not redirect here, let PDF regeneration handle page reload
        } catch (error) {
            console.error(error.message ? error.message : 'Error Saving Vault Expense');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({ message, type: 'error' });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Function to save and then regenerate PDF, to be passed to ExpenseReceiptUpload
    const saveAndRegenerateVaultPdf = async () => {
        try {
            // Regenerate PDF
            if (id) {
                const { regenerateVaultPdf } = await import('../Services/ExpenseService');
                await regenerateVaultPdf(id);
                // After PDF is regenerated, reload page to show new attachments
                window.location.reload();
            }
        } catch (error) {
            // Error handling can be improved as needed
            setAlert({ message: formatErrorMessage(error), type: 'error' });
        }
    };

    const resetImages = (index) => {
        // Resort/Reset Images
        const fileUploads = getValues(`fileUploads`);
        fileUploads.splice(index, 1);
        setValue(`fileUploads`, fileUploads);
    };

    const handleRemoveExpense = (index) => {
        // This was added to address a rendering issue
        // It turns off the TabPanel Display removes the item and then turns back on the display
        setActiveTabIndex(index);
        setRemoveTabs(true);
    };
    return (
        <Container classes={{ root: classes.ContainerRoot }}>
            <ValidationModeContext.Provider value={validationMode}>
                <FormProvider {...methods}>
                    <Grid2 container direction="column" spacing={2}>
                        <ExpenseEntryHeader
                            title={title}
                            backLink={backLink}
                            expensesPath={expensesPath}
                        />
                        {isLoading ? (
                            <CircularProgress color="primary" />
                        ) : (
                            <>
                                <Grid2 item size={{ xs: 12 }}>
                                    <form>
                                        <Grid2 container spacing={4}>
                                            <Grid2 item size={{ xs: 12, sm: 7, md: 8, lg: 9 }}>
                                                <ExpenseTabs value={activeTabIndex}>
                                                    {watchExpenseEntries.map((expense, index) => (
                                                        <ExpenseTab
                                                            key={index}
                                                            id={`expense-tab-${index}`}
                                                            label={
                                                                <ExpenseTabLabel
                                                                    isActive={
                                                                        activeTabIndex === index
                                                                    }
                                                                    index={index}
                                                                    handleRemoveTab={() =>
                                                                        handleRemoveExpense(index)
                                                                    }
                                                                    setValue={setActiveTabIndex}
                                                                    tab={expense}
                                                                    mode={ExpenseModeEnum.Vault}
                                                                    isViewOnly={isViewOnly}
                                                                    originalPrimaryExpense={
                                                                        originalPrimaryExpense
                                                                    }
                                                                    isAnyExpensePosted={
                                                                        isAnyExpensePosted
                                                                    }
                                                                />
                                                            }
                                                        />
                                                    ))}
                                                    {!isViewOnly &&
                                                        !(
                                                            isAnyExpensePosted ||
                                                            (originalPrimaryExpense &&
                                                                originalPrimaryExpense.status ===
                                                                    ExpenseStatusValuesEnum.PendingSubmission)
                                                        ) &&
                                                        !(
                                                            watchExpenseEntries[0].status ===
                                                                ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                            originalPrimaryExpense?.status ===
                                                                ExpenseStatusValuesEnum.NeedMoreInformation
                                                        )}
                                                </ExpenseTabs>
                                                {!removeTabs && (
                                                    <div>
                                                        {watchExpenseEntries.map(
                                                            (expense, index) => (
                                                                <ExpenseTabPanel
                                                                    className={classes.tabPanel}
                                                                    key={`${index}-panel`}
                                                                    value={activeTabIndex}
                                                                    index={index}
                                                                    id={`expense-panel-${index}`}
                                                                >
                                                                    <Box
                                                                        className={
                                                                            classes.iconContainer
                                                                        }
                                                                    >
                                                                        {assignmentsByIndex[
                                                                            index
                                                                        ] && (
                                                                            <Button
                                                                                variant="outlined"
                                                                                color="primary"
                                                                                href={
                                                                                    assignmentsByIndex[
                                                                                        index
                                                                                    ].sfLink
                                                                                }
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                endIcon={
                                                                                    <OpenInNew />
                                                                                }
                                                                                className={
                                                                                    classes.viewBookingButton
                                                                                }
                                                                            >
                                                                                View booking
                                                                            </Button>
                                                                        )}
                                                                        <Controller
                                                                            name={`expenseEntries[${index}].highPriority`}
                                                                            control={control}
                                                                            defaultValue={
                                                                                expense.highPriority
                                                                            }
                                                                            render={({
                                                                                onChange,
                                                                                value,
                                                                            }) => (
                                                                                <FlagIcon
                                                                                    fontSize="large"
                                                                                    className={
                                                                                        classes.flagIcon
                                                                                    }
                                                                                    onClick={() =>
                                                                                        onChange(
                                                                                            !value
                                                                                        )
                                                                                    }
                                                                                    style={{
                                                                                        color: value
                                                                                            ? COLORS.LT_PEPPER
                                                                                            : COLORS.LT_MIDNIGHT25,
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                    <div
                                                                        className={
                                                                            classes.alertContainer
                                                                        }
                                                                    >
                                                                        {expense.errors &&
                                                                            expense.errors.length >
                                                                                0 && (
                                                                                <ExpenseNeedsReviewAlert
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                />
                                                                            )}
                                                                    </div>

                                                                    <Grid2
                                                                        container
                                                                        direction="column"
                                                                        className={
                                                                            classes.formGridContainer
                                                                        }
                                                                    >
                                                                        <Grid2
                                                                            item
                                                                            size={{ xs: 12 }}
                                                                        >
                                                                            <BookingDetailsForm
                                                                                index={index}
                                                                                expense={expense}
                                                                                originalExpenseEntries={
                                                                                    originalExpenseEntries
                                                                                }
                                                                                divisions={
                                                                                    divisions
                                                                                }
                                                                                isSGA={[]}
                                                                                isViewOnly={
                                                                                    isViewOnly
                                                                                }
                                                                                onAssignmentChange={
                                                                                    handleAssignmentChange
                                                                                }
                                                                                mode={
                                                                                    ExpenseModeEnum.Vault
                                                                                }
                                                                            />
                                                                        </Grid2>
                                                                        <Grid2
                                                                            item
                                                                            size={{ xs: 12 }}
                                                                        >
                                                                            <ExpenseReceiptUploadForm
                                                                                index={index}
                                                                                expense={expense}
                                                                                originalExpenseEntries={
                                                                                    originalExpenseEntries
                                                                                }
                                                                                mode={
                                                                                    ExpenseModeEnum.Vault
                                                                                }
                                                                                expenseId={id}
                                                                                saveAndRegenerateVaultPdf={
                                                                                    saveAndRegenerateVaultPdf
                                                                                }
                                                                                pdfSuccessMessage={
                                                                                    pdfSuccessMessage
                                                                                }
                                                                                setPdfSuccessMessage={
                                                                                    setPdfSuccessMessage
                                                                                }
                                                                            />
                                                                        </Grid2>
                                                                        <Grid2
                                                                            item
                                                                            size={{ xs: 12 }}
                                                                        >
                                                                            <VaultDetailsForm
                                                                                index={index}
                                                                                expense={expense}
                                                                                originalExpenseEntries={
                                                                                    originalExpenseEntries
                                                                                }
                                                                                expenseCategories={
                                                                                    expenseCategories
                                                                                }
                                                                                isViewOnly={
                                                                                    isViewOnly
                                                                                }
                                                                                mode={
                                                                                    ExpenseModeEnum.Vault
                                                                                }
                                                                            />
                                                                        </Grid2>
                                                                        <Grid2
                                                                            item
                                                                            size={{ xs: 12 }}
                                                                        >
                                                                            <ChargeDetailsForm
                                                                                expense={expense}
                                                                                index={index}
                                                                                isViewOnly={
                                                                                    isViewOnly
                                                                                }
                                                                                mode={
                                                                                    ExpenseModeEnum.Vault
                                                                                }
                                                                            />
                                                                        </Grid2>
                                                                        <Grid2
                                                                            item
                                                                            size={{ xs: 12 }}
                                                                        >
                                                                            <InternalNotesForm
                                                                                index={index}
                                                                                expense={expense}
                                                                                originalExpenseEntries={
                                                                                    originalExpenseEntries
                                                                                }
                                                                                statusesAllowed={
                                                                                    statusesAllowed
                                                                                }
                                                                                numTabs={
                                                                                    watchExpenseEntries.length
                                                                                }
                                                                                mode={
                                                                                    ExpenseModeEnum.Vault
                                                                                }
                                                                                isViewOnly={
                                                                                    isCurrentApproved ||
                                                                                    isCurrentPosted
                                                                                }
                                                                            />
                                                                        </Grid2>
                                                                    </Grid2>
                                                                </ExpenseTabPanel>
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                            </Grid2>
                                            <Grid2 item size={{ xs: 12, sm: 5, md: 4, lg: 3 }}>
                                                <div className={classes.summary}>
                                                    <ExpenseSummary
                                                        setError={setError}
                                                        clearErrors={clearErrors}
                                                        setShowBalanceError={setShowBalanceError}
                                                        showBalanceError={showBalanceError}
                                                        setShowBalanceWarning={
                                                            setShowBalanceWarning
                                                        }
                                                        isAnyApproved={isCurrentApproved}
                                                        mode={ExpenseModeEnum.Vault}
                                                        expenseId={id}
                                                    />
                                                    {showBalanceError && (
                                                        <Typography color="error">
                                                            {errors.balance.message}
                                                        </Typography>
                                                    )}
                                                    {showBalanceWarning && (
                                                        <Typography
                                                            className={classes.warningMessage}
                                                            color="inherit"
                                                        >
                                                            Warning: The reimbursed amount exceeds
                                                            the submitted amount. Please review the
                                                            values before saving or submitting.
                                                        </Typography>
                                                    )}
                                                </div>
                                            </Grid2>
                                        </Grid2>
                                    </form>
                                </Grid2>
                                {hasErrors > 0 && (
                                    <Grid2 container className={classes.alertContainer}>
                                        <Grid2 size={{ xs: 12, sm: 7, md: 8, lg: 9 }}>
                                            <MuiAlert severity="error">
                                                Please review the highlighted fields above and
                                                correct the errors to submit your expense.
                                            </MuiAlert>
                                        </Grid2>
                                    </Grid2>
                                )}
                                {!(
                                    watchExpenseEntries.length === 1 &&
                                    watchExpenseEntries[0].status ===
                                        ExpenseStatusValuesEnum.PendingSubmission
                                ) &&
                                    !(isCurrentApproved || isCurrentPosted) && (
                                        <Grid2 item size={{ xs: 12 }}>
                                            <ExpenseButtons
                                                validateSubmit={validateSubmit}
                                                validateSave={validateSave}
                                                expensesPath={
                                                    id ? '/expenses/vault/review' : '/expenses'
                                                }
                                                mode={ExpenseModeEnum.Vault}
                                            />
                                        </Grid2>
                                    )}
                            </>
                        )}
                    </Grid2>
                </FormProvider>
                <Alert alert={alert} setAlert={setAlert} />
            </ValidationModeContext.Provider>
        </Container>
    );
};

export default withStyles(styles)(VaultPayment);
